import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { listIssueLogs } from "../../../../services/issueLogService";
import ProjectListIssues from "./ProjectListIssues";
import ProjectCardIssues from "./ProjectCardIssues";
import ProjectIssueAnalytics from "./ProjectIssueAnalytics";
import IssueDetailModal from "../ProjectUpdates/ProjectUpdateDetails/IssueDetailModal";
import AddIssueLogModal from "../ProjectUpdates/ProjectUpdateDetails/AddIssueLogModal";
import { SOURCE_OPTIONS, STATUS_OPTIONS } from "./constants";
import CAutocomplete from "../../../../components/CAutocomplete";

const PAGE_SIZE = 10;

const primaryBtn = {
  height: 40,
  padding: "0 18px",
  borderRadius: 10,
  border: "none",
  background: "#4D6BF6",
  color: "#fff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "Inter Tight",
};

export default function ProjectLogIssues({
  projectId,
  projectDetails,
  isMobile,
  // Contractor-side overrides. When `activitiesOverride` is provided we
  // skip deriving activities from `projectDetails.milestones` and use the
  // caller's list verbatim (e.g. flattened deliverable descriptions). When
  // `roomAsText` is true the modal swaps the room dropdown for a free-text
  // input and skips the rooms fetch entirely.
  activitiesOverride,
  roomAsText = false,
  // Default source-type filter + the source_type sent on create. Used by
  // the contractor side to pin the listing/filter to CONTRACTOR and to
  // tag newly logged issues with the same enum.
  sourceTypeOverride = "",
  // When true the create payload omits the `activity` object entirely
  // (and skips the activity-required validation in the modal).
  omitActivity = false,
}) {
  const [status, setStatus] = useState("");
  const [sourceType, setSourceType] = useState(sourceTypeOverride || "");
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const activities = useMemo(() => {
    if (Array.isArray(activitiesOverride)) return activitiesOverride;
    const milestones = projectDetails?.milestone || projectDetails?.milestones || [];
    return milestones.map((m) => ({
      id: m.id,
      milestone_name: m.milestone_name || m.name || `Milestone ${m.id}`,
    }));
  }, [projectDetails, activitiesOverride]);

  const loadList = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await listIssueLogs(projectId, {
        page,
        size: PAGE_SIZE,
        sortBy: "date",
        sortDirection: "DESC",
        status: status || undefined,
        sourceType: sourceType || undefined,
      });
      setPageData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, status, sourceType, page]);

  const issues = pageData?.content ?? [];
  const totalPages = pageData?.totalPages ?? 0;

  const handleChanged = () => {
    setSelectedIssueId(null);
    loadList();
  };

  const selectedStatus =
    STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  const selectedSource =
    SOURCE_OPTIONS.find((o) => o.value === sourceType) || SOURCE_OPTIONS[0];

  return (
    <div style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
      <style>{`
        @media (max-width: 899px) {
          .issues-header-row { flex-wrap: wrap; }
          .issues-header-filters {
            order: 2;
            width: 100%;
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
          }
          .issues-header-filters .issues-log-btn { display: none; }
          .issues-header-title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }
          .issues-header-title-row .issues-log-btn { display: inline-flex; align-items: center; }
        }
        @media (min-width: 900px) {
          .issues-header-title-row .issues-log-btn { display: none; }
        }
      `}</style>
      <div
        className="issues-header-row"
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "0 12px" : "0 4px",
          marginBottom: 8,
        }}
      >
        <div
          className="issues-header-title-row"
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: "#0d0d0e",
            fontFamily: "Inter Tight",
          }}
        >
          <span>Issues</span>
          {/* Mobile-only Log Issue — sits inline with the heading. */}
          <button
            type="button"
            className="issues-log-btn"
            style={primaryBtn}
            onClick={() => setLogOpen(true)}
          >
            Log Issue
          </button>
        </div>

        <div
          className="issues-header-filters"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <CAutocomplete
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(_e, v) => {
                setStatus(v?.value ?? "");
                setPage(0);
              }}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              disableClearable
              placeholder="All"
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <CAutocomplete
              options={SOURCE_OPTIONS}
              value={selectedSource}
              onChange={(_e, v) => {
                setSourceType(v?.value ?? "");
                setPage(0);
              }}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              disableClearable
              placeholder="All Sources"
            />
          </div>
          <button
            type="button"
            className="issues-log-btn"
            style={primaryBtn}
            onClick={() => setLogOpen(true)}
          >
            Log Issue
          </button>
        </div>
      </div>

      <ProjectIssueAnalytics projectId={projectId} />

      {loading && !pageData && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && issues.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "#6b7280",
            fontSize: 14,
          }}
        >
          No issues logged.
        </div>
      )}

      {issues.length > 0 &&
        (isMobile ? (
          <ProjectCardIssues list={issues} onIssueClick={setSelectedIssueId} />
        ) : (
          <ProjectListIssues list={issues} onIssueClick={setSelectedIssueId} />
        ))}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "12px" : "16px 4px",
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            Page {page + 1} of {totalPages}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={pageData?.first}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: pageData?.first ? "#f3f4f6" : "#fff",
                cursor: pageData?.first ? "not-allowed" : "pointer",
                opacity: pageData?.first ? 0.6 : 1,
                fontSize: 13,
              }}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pageData?.last}
              onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: pageData?.last ? "#f3f4f6" : "#fff",
                cursor: pageData?.last ? "not-allowed" : "pointer",
                opacity: pageData?.last ? 0.6 : 1,
                fontSize: 13,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedIssueId && (
        <IssueDetailModal
          issueId={selectedIssueId}
          onClose={() => setSelectedIssueId(null)}
          onChanged={handleChanged}
        />
      )}

      {logOpen && (
        <AddIssueLogModal
          projectId={projectId}
          updateId={null}
          activities={activities}
          roomAsText={roomAsText}
          sourceType={sourceTypeOverride || undefined}
          omitActivity={omitActivity}
          onClose={() => setLogOpen(false)}
          onSaved={() => {
            setLogOpen(false);
            loadList();
          }}
        />
      )}
    </div>
  );
}
